import {useEffect} from "react";
import {cats} from "../helper/constant";
import {isCommonPage} from "../containers/LayoutContainer/is-home-page";

const DEFAULT_TITLE = "Delishop - First Online Supermarket in Cambodia";
const DEFAULT_DESCRIPTION = "Get all your favorite products from the best shops of Phnom Penh. More than 10,000 products to choose from";
const DEFAULT_IMAGE = "https://delishop.s3.ap-southeast-1.amazonaws.com/app/photo_2023-04-12+11.07.52.jpeg";
const SITE_URL = "https://delishop.asia";

const setMetaTag = (name, content) => {
	const metaTag = document.getElementsByTagName("meta").namedItem(name);
	if (metaTag) {
		metaTag.setAttribute("content", content);
	}
};

const setPropertyTag = (property, content) => {
	let metaTag = document.querySelector(`meta[property='${property}']`);
	if (!metaTag) {
		metaTag = document.createElement("meta");
		metaTag.setAttribute("property", property);
		document.head.appendChild(metaTag);
	}
	metaTag.setAttribute("content", content);
};

const setPageMetadata = (title, description, image = DEFAULT_IMAGE, url = null) => {
	// Basic meta tags
	document.title = title;
	setMetaTag("description", description);
	setMetaTag("title", title);

	// Open Graph tags for social media
	setPropertyTag("og:title", title);
	setPropertyTag("og:description", description);
	setPropertyTag("og:image", image);
	setPropertyTag("og:type", "website");

	if (url) {
		setPropertyTag("og:url", url);
	}

	// Twitter Card tags
	setMetaTag("twitter:card", "summary_large_image");
	setMetaTag("twitter:title", title);
	setMetaTag("twitter:description", description);
	setMetaTag("twitter:image", image);
};

const createCanonicalLink = () => {
	let link = document.querySelector("link[rel='canonical']");
	if (!link) {
		link = document.createElement("link");
		link.setAttribute("rel", "canonical");
		document.head.appendChild(link);
	}
	return link;
};

export const useSEO = () => {
	useEffect(() => {
		const mql = window.matchMedia("all and (min-width: 769px)");
		if (!mql.matches) return;

		const canonicalLink = createCanonicalLink();
		const currentUrl = `${SITE_URL}${window.location.pathname}`;
		canonicalLink.setAttribute("href", currentUrl);

		const pathSegments = window.location.pathname.split("/").filter(Boolean);
		const firstSegment = pathSegments[0];
		const secondSegment = pathSegments[1];

		// Search page
		if (firstSegment === "search" && secondSegment) {
			const searchTitle = `${secondSegment} - Delishop`;
			const searchUrl = `${SITE_URL}/search/${secondSegment}`;
			setPageMetadata(searchTitle, searchTitle, DEFAULT_IMAGE, searchUrl);
			return;
		}

		// Category page
		if (cats.includes(firstSegment)) {
			const categoryTitle = `${firstSegment} - Delishop`;
			const categoryUrl = `${SITE_URL}/${firstSegment}`;
			setPageMetadata(categoryTitle, categoryTitle, DEFAULT_IMAGE, categoryUrl);
			return;
		}

		// Deep path (length > 2)
		if (pathSegments.length > 2) {
			const deepTitle = `${secondSegment} - Delishop`;
			setPageMetadata(deepTitle, deepTitle, DEFAULT_IMAGE, currentUrl);
			return;
		}

		// Single segment common page or default
		if (
			pathSegments.length <= 2 &&
			firstSegment &&
			(isCommonPage(firstSegment) || !firstSegment)
		) {
			setPageMetadata(DEFAULT_TITLE, DEFAULT_DESCRIPTION, DEFAULT_IMAGE, SITE_URL);
			return;
		}

		// Default fallback
		setPageMetadata(DEFAULT_TITLE, DEFAULT_DESCRIPTION, DEFAULT_IMAGE, currentUrl);
	}, []);
};

/**
 * Hook for dynamic page-specific SEO (partner, landing, product pages)
 * @param {Object} pageData - { title, description, image, type }
 */
export const usePageSEO = (pageData) => {
	useEffect(() => {
		if (!pageData || !pageData.title) return;

		const canonicalLink = createCanonicalLink();
		const currentUrl = `${SITE_URL}${window.location.pathname}`;
		canonicalLink.setAttribute("href", currentUrl);

		const {title, description, image, type} = pageData;
		const seoTitle = `${title} | Delishop`;
		const seoDescription = description || `Shop ${title} products at Delishop - First Online Supermarket in Cambodia`;
		const seoImage = image || DEFAULT_IMAGE;

		setPageMetadata(seoTitle, seoDescription, seoImage, currentUrl);

		// Add JSON-LD structured data for partner/organization pages
		if (type === "partner" || type === "landing") {
			addStructuredData({
				"@context": "https://schema.org",
				"@type": "Organization",
				"name": title,
				"description": seoDescription,
				"url": currentUrl,
				"logo": image,
				"parentOrganization": {
					"@type": "Organization",
					"name": "Delishop",
					"url": SITE_URL
				}
			});
		}
	}, [pageData]);
};

/**
 * Add JSON-LD structured data to page
 */
const addStructuredData = (data) => {
	// Remove existing structured data
	const existing = document.getElementById("structured-data");
	if (existing) {
		existing.remove();
	}

	// Add new structured data
	const script = document.createElement("script");
	script.id = "structured-data";
	script.type = "application/ld+json";
	script.textContent = JSON.stringify(data);
	document.head.appendChild(script);
};
